/**
 * Display-only copy and outbound links for a connector's detail view, keyed by catalog key.
 * Web-side on purpose: no server reads it, and the curated catalog stays free of marketing
 * prose. connector-content.test.ts asserts one entry per real CONNECTION_CATALOG key.
 */

export interface ConnectorLink {
  label: string;
  href: string;
}

export interface ConnectorContent {
  /** Long-form overview shown while the connector is not connected yet. */
  overview: string;
  /** What to ask Spec once it is connected, with example prompts. */
  howToUse: string;
  /** The provider itself, linked to the page it markets this product on. */
  author: ConnectorLink;
  /** Documentation, support, and privacy links, in display order. */
  moreInfo: readonly ConnectorLink[];
}

const MICROSOFT_PRIVACY = 'https://www.microsoft.com/privacy/privacystatement';
const MICROSOFT_SUPPORT = 'https://support.microsoft.com/contactus';

// A Map, not an object literal, for the same reason as CATALOG_ICON_DOMAINS: a catalog key
// named e.g. 'constructor' must miss, never resolve to an inherited Object.prototype value.
const CONNECTOR_CONTENT: ReadonlyMap<string, ConnectorContent> = new Map([
  [
    'hubspot',
    {
      overview:
        "Work your HubSpot CRM alongside Parcel's project signals. Search contacts, companies, and deals, create and update records, and keep owners current without leaving Spec.",
      howToUse:
        'Ask Spec to move between a signal and your pipeline. Try prompts like "find the HubSpot company for this account", "create a deal for this project", or "update that contact with the title I just found".',
      author: { label: 'HubSpot', href: 'https://www.hubspot.com' },
      moreInfo: [
        { label: 'Documentation', href: 'https://knowledge.hubspot.com' },
        { label: 'Support', href: 'https://help.hubspot.com' },
        { label: 'Privacy Policy', href: 'https://legal.hubspot.com/privacy-policy' },
      ],
    },
  ],
  [
    'microsoft_teams',
    {
      overview:
        'Bring Teams conversations into the same place as your project signals. Read channel messages and chats, and post updates back to the channel your team already watches.',
      howToUse:
        'Ask Spec to share what it found. Try prompts like "post this week\'s new permits to our BD channel", "what have we said about this account in Teams", or "send this project summary to my chat with the design lead".',
      author: {
        label: 'Microsoft',
        href: 'https://www.microsoft.com/en-us/microsoft-teams/group-chat-software',
      },
      moreInfo: [
        { label: 'Documentation', href: 'https://support.microsoft.com/teams' },
        { label: 'Support', href: MICROSOFT_SUPPORT },
        { label: 'Privacy Policy', href: MICROSOFT_PRIVACY },
      ],
    },
  ],
  [
    'outlook',
    {
      overview:
        'Search Outlook mail and calendar next to your account and project data. Look up a thread with a contact, check what is on the calendar, and manage contacts and events from Spec.',
      howToUse:
        'Ask Spec to connect an account to your inbox. Try prompts like "find my last email thread with this contact", "schedule a follow up on this project", or "add the contact I just found to Outlook".',
      author: {
        label: 'Microsoft',
        href: 'https://www.microsoft.com/en-us/microsoft-365/outlook/email-and-calendar-software-microsoft-outlook',
      },
      moreInfo: [
        { label: 'Documentation', href: 'https://support.microsoft.com/outlook' },
        { label: 'Support', href: MICROSOFT_SUPPORT },
        { label: 'Privacy Policy', href: MICROSOFT_PRIVACY },
      ],
    },
  ],
  [
    'notion',
    {
      overview:
        'Keep your Notion workspace in step with what Parcel finds. Search pages and database rows, add and update entries, and leave comments from Spec.',
      howToUse:
        'Ask Spec to write findings where your team already reads them. Try prompts like "add this project to our pursuit tracker", "find the Notion page for this account", or "comment on that page with the latest permit status".',
      author: { label: 'Notion', href: 'https://www.notion.com' },
      moreInfo: [
        { label: 'Documentation', href: 'https://www.notion.com/help' },
        {
          label: 'Privacy Policy',
          href: 'https://www.notion.so/notion/Privacy-Policy-3468d120cf614d4c9014c09f6adc9091',
        },
      ],
    },
  ],
  [
    'asana',
    {
      overview:
        'Turn a signal into work your team can pick up. Search Asana tasks and projects, create and update tasks, and comment without switching tools.',
      howToUse:
        'Ask Spec to open the loop on a pursuit. Try prompts like "create an Asana task to follow up on this project", "what is still open for this account", or "comment on that task with the contact I just found".',
      author: { label: 'Asana', href: 'https://asana.com' },
      moreInfo: [
        { label: 'Documentation', href: 'https://help.asana.com' },
        { label: 'Support', href: 'https://asana.com/support' },
        { label: 'Privacy Policy', href: 'https://asana.com/terms#privacy-policy' },
      ],
    },
  ],
  [
    'slack',
    {
      overview:
        'Work Slack alongside your account and project data. Search channels and messages, post updates, and correct a message you already sent.',
      howToUse:
        'Ask Spec to put a signal in front of the right channel. Try prompts like "post the new projects in my saved view to the BD channel", "what has been said about this account in Slack", or "update that message with the owner I just confirmed".',
      author: { label: 'Slack', href: 'https://slack.com' },
      moreInfo: [
        { label: 'Documentation', href: 'https://slack.com/help' },
        { label: 'Support', href: 'https://slack.com/help/contact' },
        { label: 'Privacy Policy', href: 'https://slack.com/trust/privacy/privacy-policy' },
      ],
    },
  ],
  [
    'salesforce',
    {
      overview:
        "Work your Salesforce org alongside Parcel's project signals. Search and manage accounts, contacts, leads, and opportunities, and log the activity behind them from Spec.",
      howToUse:
        'Ask Spec to move between a signal and your pipeline. Try prompts like "find the Salesforce account for this company", "create an opportunity for this project", or "add the contact I just found as a lead".',
      author: { label: 'Salesforce', href: 'https://www.salesforce.com' },
      moreInfo: [
        { label: 'Documentation', href: 'https://help.salesforce.com' },
        { label: 'Support', href: 'https://help.salesforce.com/s/support' },
        { label: 'Privacy Policy', href: 'https://www.salesforce.com/company/privacy/' },
      ],
    },
  ],
]);

/** Null for any key with no curated copy, including a catalog entry added before its copy. */
export function connectorContent(catalogKey: string): ConnectorContent | null {
  return CONNECTOR_CONTENT.get(catalogKey) ?? null;
}

export function connectorContentKeys(): readonly string[] {
  return [...CONNECTOR_CONTENT.keys()];
}
